/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import { Setting, SETTING_TYPE, Advisor } from '@circuitry-ai/doc-data';
import { applicationType, ApplicationType, isBrowserExtension } from './constants';
import LZString from 'lz-string';
interface PdfLoadingState {
  [key: string]: {
    view: boolean;
    filename: boolean;
  };
}

export interface pdfViewPayload {
  advisor_id: string;
  source_ids: [
    {
      source_id: string;
      page_labels?: number[] | [];
    }
  ];
}

export interface AgentViewPayload {
  source_ids: [
    {
      source_id: string;
      file_name: string;
      file_extension: string;
    }
  ];
}

const formatPageLabels = (pageLabels: string[]): string => {
  if (!pageLabels || pageLabels.length === 0) {
    return '';
  }

  if (pageLabels.length === 1) {
    return pageLabels[0];
  }

  const firstPage = parseInt(pageLabels[0], 10);
  const lastPage = parseInt(pageLabels[pageLabels.length - 1], 10);

  const lowerPage = Math.min(firstPage, lastPage);
  const higherPage = Math.max(firstPage, lastPage);

  return `${lowerPage}-${higherPage}`;
};
/** Parses a raw timestamp value (seconds) into a non-negative finite number, or null. */
const parseClipSeconds = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/** Formats seconds as m:ss (h:mm:ss when >=1h). Empty string for invalid input. */
const formatClipSeconds = (raw: unknown): string => {
  const n = parseClipSeconds(raw);
  if (n === null) return '';
  const total = Math.floor(n);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = s.toString().padStart(2, '0');
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
};

/**
 * Builds a "[start - end]" clip-time label from a source's element_refs[0].start_time/end_time.
 * Returns empty string when neither is present/valid.
 */
const getSourceClipTimeLabel = (source: any): string => {
  const start = formatClipSeconds(source?.element_refs?.[0]?.start_time);
  const end = formatClipSeconds(source?.element_refs?.[0]?.end_time);
  if (!start && !end) return '';
  if (start && end) return ` [${start} - ${end}]`;
  return ` [${start || end}]`;
};

/**
 * Appends a Media Fragments URI temporal fragment (`#t=start,end`) to a media URL so the browser's
 * native player seeks to `start` on load and stops at `end`. Keeps the original URL when neither
 * timestamp is present, and preserves the presigned query string (fragment is appended after it).
 */
const buildMediaFragmentUrl = (url: string, start?: unknown, end?: unknown): string => {
  if (!url) return url;
  const s = parseClipSeconds(start);
  const e = parseClipSeconds(end);
  if (s === null && e === null) return url;
  const base = url.split('#')[0];
  const startPart = s !== null ? String(s) : '';
  const endPart = e !== null ? `,${e}` : '';
  return `${base}#t=${startPart}${endPart}`;
};

/**
 * Opens a media (video/audio) file in a tab using the native browser player at its real URL.
 * When start/end timestamps are provided, a temporal media fragment (`#t=start,end`) is appended so
 * the player seeks to start and stops at end — preserving the original tab URL and default dimensions.
 */
const openMediaViewer = (
  transcribeTimestamps: string[] | undefined,
  fullPdfUrl: string,
  setIsTableFileLoading?: any,
  existingWindow?: Window | null,
) => {
  const mediaUrl = buildMediaFragmentUrl(fullPdfUrl, transcribeTimestamps?.[0], transcribeTimestamps?.[1]);

  if (existingWindow && !existingWindow.closed) {
    existingWindow.location.href = mediaUrl;
  } else {
    window.open(mediaUrl, '_blank');
  }

  if (typeof setIsTableFileLoading === 'function') {
    setIsTableFileLoading(false);
  }
};

const handleKnowledgePdfViewer = async (
  source: any,
  CtyShowToast: any,
  checkType: any,
  getPdfLink: any,
  setIsTableFileLoading: React.Dispatch<React.SetStateAction<PdfLoadingState>> | any,
  loadingType: 'view' | 'filename',
  transcribeTimestamps?: string[]
) => {
  transcribeTimestamps && transcribeTimestamps?.length > 0 ? setIsTableFileLoading(transcribeTimestamps[0]) :
    setIsTableFileLoading((prevState) => ({
      ...prevState,
      [source?.source_id]: {
        ...prevState[source?.source_id],
        [loadingType]: true,
      },
    }));
  try {
    const getPdfLinkRequestPayload = {
      source_ids: [
        {
          source_id: source?.source_id,
        },
      ],
    };
    const urlReponse = await getPdfLink(getPdfLinkRequestPayload).unwrap();

    const fullPdfUrl = urlReponse?.data?.[0]?.full_source_presigned_url;
    const sourceFileName = urlReponse?.data?.[0]?.filename;
    switch (urlReponse?.data?.[0].source_extension) {
      case 'mp4':
      case 'webm':
      case 'ogg':
      case 'mov':
      case 'avi':
      case 'flv':
      case 'mkv':
      case 'mp3':
      case 'wav':
      case 'aac':
      case 'flac':
      case 'wma':
      case 'opus':
      case 'aiff':
      case 'alac':
      case 'amr': {
        openMediaViewer(transcribeTimestamps, fullPdfUrl, setIsTableFileLoading);
        return null;
      }

      case 'html':
      case 'htm':
      case 'xml':
      case 'url':
      case 'xlsx':
      case 'xls':
      case 'docx':
      case 'csv':
      case 'pptx':
      case 'ppt':
        window.open(fullPdfUrl, '_blank');
        return null;
      default:
        break;
    }

    const tabKey = Date.now().toString();
    const pdfData = {
      initialPdfUrl: fullPdfUrl,
      fullPdfUrl: fullPdfUrl,
      fileType: urlReponse?.data?.[0].source_extension,
      fileName: sourceFileName,
      transcribeTimestamps: transcribeTimestamps,
      sourceId: source?.source_id,
    };
    const { advisorId } = (fullPdfUrl &&
      extractAdvisorIdAndFilename(fullPdfUrl)) ?? {
      advisorId: '',
    };

    const packed = compressedData(pdfData);

    if (applicationType() === ApplicationType.WEB) {
      sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
      window.open(`/file/${advisorId}/${encodeURIComponent(sourceFileName)}?tabKey=${tabKey}`, '_blank');
    } else {
      window.open(`/file/${advisorId}/${encodeURIComponent(sourceFileName)}?tabKey=${tabKey} `, '_blank');
    }
  } catch (e) {
    CtyShowToast(checkType.error, (e as Error)?.message ?? 'Error while getting pdf link');
  } finally {
    transcribeTimestamps && transcribeTimestamps.length > 0 ? setIsTableFileLoading('0') : setIsTableFileLoading((prevState) => ({
      ...prevState,
      [source?.source_id]: {
        ...prevState[source?.source_id],
        [loadingType]: false,
      },
    }));
  }
};
const generateCircuitryLogoSvgBlobUrl = (color: string) => {
  //TODO: add support for other SVG files
  const svgString = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 267.13 344.22">
          <defs>
            <style>
              .cls-1 {
                fill: ${color};
                stroke-width: 0px;
              }
            </style>
          </defs>
          <g id="Layer_1-2" data-name="Layer 1">
            <g>
              <circle class="cls-1" cx="247.87" cy="247.43" r="19.25"/>
              <path class="cls-1" d="M156.41,338.52c-7.55-7.58-7.53-19.84.04-27.39l36.4-36.31c7.58-7.55,19.84-7.53,27.39.04,7.55,7.58,7.53,19.84-.04,27.39l-36.4,36.31c-7.58,7.55-19.84,7.53-27.39-.04Z"/>
              <path class="cls-1" d="M149.58,290.76c-11.28,11.26-29.55,11.24-40.82-.04h-.01c-11.25-11.3-11.22-29.56.05-40.82l70.99-70.85c15.93-16.21,23.25-39.25,19.55-61.64-6.39-38.78-43.16-65.11-81.93-58.72-38.78,6.4-65.11,43.16-58.72,81.93,1.33,8.05,5.69,23.69,19.98,38.04,3.01,3.02,17.07,17.28,17.07,17.28l-.02.02c11.04,11.31,10.93,29.41-.28,40.58-11.3,11.26-29.56,11.23-40.81-.05-.29-.29-14.37-14.59-16.85-17.07C18.78,200.34,5.98,175.69,1.73,150.02-9.84,79.83,37.83,13.31,108.01,1.73c70.18-11.58,136.71,36.09,148.29,106.28,6.7,40.59-6.56,82.33-35.48,111.65l-.16.16-71.07,70.94Z"/>
            </g>
          </g>
        </svg>
    `;

  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  return URL.createObjectURL(blob);
};

const aggridCommonGridStyles = {
  height: '100%',
  width: '100%',
  '--ag-border-color': 'transparent', // Remove the border
  '--ag-header-background-color': 'white', // Add this line
  '--ag-borders': 'none',
  /* then add back a border between rows */
  '--ag-row-border-color': 'transperent',
  /* and between columns */
  '--ag-cell-horizontal-border': 'transperent',
  '--ag-header-column-separator-color': 'transparent',
  '--ag-odd-row-background-color': '#F9FAFB',
  '--ag-row-hover-color': '#F0F9FF',
  '--ag-checkbox-checked-color': 'rgb(0 58 121)',
  '--ag-range-selection-border-color': 'transparent',
  '--ag-scrollbar-thickness': '30px',
  '--ag-scrollbar-thumb-color': 'rgba(0, 0, 0, 0.5)',
};
function extractAdvisorIdAndFilename(url: string) {
  try {
    // Create a URL object
    const urlObj = new URL(url);

    // Split the pathname of the URL to extract parts
    const pathParts = urlObj.pathname.split('/');
    // The advisorId is located at index 3, and the filename is at index 4
    const advisorId = pathParts[2];
    const filename = pathParts[3];

    return { advisorId, filename };
  } catch (error) {
    console.error('Invalid URL format:', error);
    return null;
  }
}
const injectLoadingHTML = (windowRef: Window | null, fileName?: string) => {
  if (windowRef && !windowRef.closed) {
    const isDark =
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark');
    const theme = isDark
      ? { bg: '#0D1117', text: '#c4c4c4', track: '#30363d', accent: '#58a6ff' }
      : { bg: '#ffffff', text: '#6b7280', track: '#f0f0f0', accent: '#003a79' };
    windowRef.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Circuitry.ai</title>
        <link rel="icon" type="image/svg+xml" href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 267.13 344.22'%3E%3Cdefs%3E%3Cstyle%3E.cls-1%7Bfill:%23d4007e;stroke-width:0px;%7D%3C/style%3E%3C/defs%3E%3Cg id='Layer_1-2' data-name='Layer 1'%3E%3Cg%3E%3Ccircle class='cls-1' cx='247.87' cy='247.43' r='19.25'/%3E%3Cpath class='cls-1' d='M156.41,338.52c-7.55-7.58-7.53-19.84.04-27.39l36.4-36.31c7.58-7.55,19.84-7.53,27.39.04,7.55,7.58,7.53,19.84-.04,27.39l-36.4,36.31c-7.58,7.55-19.84,7.53-27.39-.04Z'/%3E%3Cpath class='cls-1' d='M149.58,290.76c-11.28,11.26-29.55,11.24-40.82-.04h-.01c-11.25-11.3-11.22-29.56.05-40.82l70.99-70.85c15.93-16.21,23.25-39.25,19.55-61.64-6.39-38.78-43.16-65.11-81.93-58.72-38.78,6.4-65.11,43.16-58.72,81.93,1.33,8.05,5.69,23.69,19.98,38.04,3.01,3.02,17.07,17.28,17.07,17.28l-.02.02c11.04,11.31,10.93,29.41-.28,40.58-11.3,11.26-29.56,11.23-40.81-.05-.29-.29-14.37-14.59-16.85-17.07C18.78,200.34,5.98,175.69,1.73,150.02-9.84,79.83,37.83,13.31,108.01,1.73c70.18-11.58,136.71,36.09,148.29,106.28,6.7,40.59-6.56,82.33-35.48,111.65l-.16.16-71.07,70.94Z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E">
        <style>
          body { display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100vh; margin: 0; background: ${theme.bg}; font-family: system-ui, -apple-system, sans-serif; }
          .spinner { width: 40px; height: 40px; border: 3px solid ${theme.track}; border-top-color: ${theme.accent}; border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 16px; }
          .loading-text { color: ${theme.text}; font-size: 14px; text-align: center; }
          @keyframes spin { to { transform: rotate(360deg); } }
        </style>
      </head>
      <body>
        <div class="spinner"></div>
        <div class="loading-text">Fetching the file${fileName ? ` ${fileName}` : ''}...</div>
      </body>
      </html>
    `);
    windowRef.document.close();
  }
};

// Helper function to conditionally open preOpened tab (only for WEB and WIDGET)
const openPreOpenedTab = (fileName?: string): Window | null => {
  const preOpened = applicationType() === ApplicationType.BROWSER_EXTENSION 
    ? null 
    : window.open('about:blank', '_blank');
  if (preOpened) {
    injectLoadingHTML(preOpened, fileName);
  }
  return preOpened;
};

// Helper function to close preOpened tab safely
const closePreOpenedTab = (preOpened: Window | null) => {
  if (preOpened && !preOpened.closed) {
    try {
      preOpened.close();
    } catch { /* noop */ }
  }
};

// Helper function to store in extension storage and open extension tab
const openExtensionTab = async (tabKey: string, fileViewerUrl: string, packed: string, preOpened: Window | null) => {
  if ((globalThis as any).chrome?.storage?.session) {
    try {
      await (globalThis as any).chrome.storage.session.set({
        [`pdfData-${tabKey}`]: packed
      });
    } catch (error) {
      console.warn('⚠️ Failed to store in extension storage:', error);
    }
  }
  closePreOpenedTab(preOpened);
  //@ts-expect-error
  chrome.tabs.create({ url: fileViewerUrl });
};

/** Build PAL chatbot script URL from CDN (org=dev -> dev.cdn.circuitry.ai, org="" -> cdn.circuitry.ai). */
const getPalChatbotScriptUrl = (): string => {
  if (!import.meta.env.CTY_PAL_CDN_URL) return '';
  return String(import.meta.env.CTY_PAL_CDN_URL ?? '').trim() + '/pal/pal-chatbot.umd.js';
}

const getEnv = (): string => {
  const mode: string = (import.meta as any).env?.CTY_MODE;
  if (mode  === 'development') {
    return 'dev';
  } else if (mode  === 'staging') 
    return 'staging';
  return '';
}

/** Org for pal-chatbot embed — drives PAL iframe host (e.g. cars.sandbox → cars.sandbox.pal.circuitry.ai). */
const getPalEmbedOrg = (): string => {
  if (globalThis.window === undefined) {
    return '';
  }
  const orgName = globalThis.window.location.hostname.replace(/\.circuitry\.ai$/, '');
  if (orgName === 'app') {
    return '';
  }
  if (orgName === 'localhost') {
    return 'dev';
  }
  return orgName;
};

/** PAL web app base URL — same org as widget/HTML iframe (`cars.sandbox` → cars.sandbox.pal). */
const getPalUrl = (): string | undefined => {
  if (globalThis.window === undefined) {
    return undefined;
  }
  const org = getPalEmbedOrg();
  return org ? `https://${org}.pal.circuitry.ai/` : 'https://pal.circuitry.ai/';
};

export type PalChatbotMode = 'widget' | 'dock';

/** TEMPORARY: hendrick tenants always use dock (in-app and HTML embed). Remove when no longer needed. */
const FORCE_PAL_DOCK_ORG = 'hendrick';

export const shouldForcePalDockMode = (orgName?: string | null): boolean =>
  (orgName ?? '').trim().toLowerCase() === FORCE_PAL_DOCK_ORG;

export const resolvePalModeForTenant = (
  orgName: string | null | undefined,
  fallback: PalChatbotMode
): PalChatbotMode => (shouldForcePalDockMode(orgName) ? 'dock' : fallback);

type ShowingPalAgent = { metadata?: unknown };

/** Returns only agents explicitly enabled for PAL through `metadata.showingPal`. */
export const filterShowingPalAgents = <T extends ShowingPalAgent>(
  agents: T[] | null | undefined
): T[] => (agents ?? []).filter((agent) => {
  let metadata = agent.metadata;
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      return false;
    }
  }
  return Boolean(
    metadata
    && typeof metadata === 'object'
    && (metadata as Record<string, unknown>).showingPal === true
  );
});

/** In-memory In APP presentation — follows the tenant default advisor. */
let palSessionMode: PalChatbotMode | null = null;

const PAL_DEFAULT_MODE_CACHE_KEY = 'palDefaultAdvisorView';

export const getCachedDefaultAdvisorPalMode = (): PalChatbotMode | null => {
  try {
    const stored = sessionStorage.getItem(PAL_DEFAULT_MODE_CACHE_KEY);
    if (stored === 'dock' || stored === 'widget') {
      return stored;
    }
  } catch {
    // ignore storage failures
  }
  return null;
};

export const cacheDefaultAdvisorPalMode = (mode: PalChatbotMode): void => {
  if (mode !== 'dock' && mode !== 'widget') {
    return;
  }
  try {
    sessionStorage.setItem(PAL_DEFAULT_MODE_CACHE_KEY, mode);
  } catch {
    // ignore storage failures
  }
};

export const parseAdvisorPalView = (metadata: unknown): PalChatbotMode => {
  if (metadata == null) {
    return 'widget';
  }
  let record: unknown = metadata;
  if (typeof metadata === 'string') {
    try {
      record = JSON.parse(metadata);
    } catch {
      return 'widget';
    }
  }
  if (typeof record !== 'object' || record === null) {
    return 'widget';
  }
  const palview = (record as { palview?: unknown }).palview;
  return palview === 'dock' ? 'dock' : 'widget';
};

/** Merge palview into existing advisor metadata for PATCH. */
export const mergeAdvisorPalMetadata = (
  existing: unknown,
  palview: PalChatbotMode
): Record<string, unknown> => {
  let base: Record<string, unknown> = {};
  if (existing && typeof existing === 'object') {
    base = { ...(existing as Record<string, unknown>) };
  } else if (typeof existing === 'string') {
    try {
      const parsed = JSON.parse(existing);
      if (parsed && typeof parsed === 'object') {
        base = { ...(parsed as Record<string, unknown>) };
      }
    } catch {
      // ignore invalid JSON
    }
  }
  return { ...base, palview };
};

/** @deprecated use parseAdvisorPalView on advisor.metadata */
export const parseAdvisorEmbedMode = (value: unknown): PalChatbotMode => {
  if (value === 'dock' || value === 'widget') {
    return value;
  }
  return parseAdvisorPalView(value);
};

export const getPalSessionMode = (): PalChatbotMode =>
  palSessionMode ?? getCachedDefaultAdvisorPalMode() ?? 'widget';

export const setPalSessionMode = (mode: PalChatbotMode): void => {
  if (mode === 'dock' || mode === 'widget') {
    palSessionMode = mode;
  }
};

export const clearPalSessionMode = (): void => {
  palSessionMode = null;
};

/** @deprecated use getPalSessionMode */
export const getStoredPalEmbedMode = getPalSessionMode;

/** @deprecated use setPalSessionMode */
export const persistPalEmbedMode = setPalSessionMode;

/** Resolve PAL view from advisor.metadata.palview (all tenants). Default widget when null. */
export const resolveAdvisorEmbedModeFromRecord = (
  advisor?: { metadata?: unknown } | null
): PalChatbotMode => parseAdvisorPalView(advisor?.metadata);

type AdvisorPalRecord = {
  advisor_id?: string;
  is_default?: string;
  metadata?: unknown;
};

/** PAL in-app always opens the tenant default advisor first. */
export const resolveDefaultAdvisor = <T extends AdvisorPalRecord>(
  advisors: T[],
  activeAdvisor?: T | null
): T | null => {
  const fromList = advisors.find((item) => item.is_default === '1');
  if (fromList) {
    if (activeAdvisor && activeAdvisor.advisor_id === fromList.advisor_id) {
      return activeAdvisor;
    }
    return fromList;
  }
  if (activeAdvisor?.is_default === '1') {
    return activeAdvisor;
  }
  return advisors[0] ?? activeAdvisor ?? null;
};

/**
 * Resolve PAL embed mode, preferring the tenant default advisor (in-app PAL).
 * Falls back to the active advisor, then the first advisor in the list.
 */
export const resolvePalTargetMode = async (
  advisors: Advisor[],
  activeChatAdvisor?: Advisor | null
): Promise<PalChatbotMode> => {
  const advisor = resolveDefaultAdvisor(advisors, activeChatAdvisor);
  if (!advisor) {
    return 'widget';
  }
  return resolveAdvisorEmbedModeFromRecord(advisor);
};

/**
 * Put the host `mode` on `<pal-chatbot>` immediately so dock/widget does not wait for
 * the iframe advisor response (that wait is what causes the layout jump).
 */
export const applyPalChatbotHostMode = (mode: PalChatbotMode): void => {
  if (mode !== 'dock' && mode !== 'widget') {
    return;
  }
  setPalSessionMode(mode);
  cacheDefaultAdvisorPalMode(mode);
  const palChatbotElement = document.querySelector('pal-chatbot');
  if (!palChatbotElement) {
    return;
  }
  if (palChatbotElement.getAttribute('mode') !== mode) {
    palChatbotElement.setAttribute('mode', mode);
  }
};

export type InjectPalChatbotOptions = {
  /** Open floating widget after inject. Default true. */
  open?: boolean;
  /**
   * Called before open so `mode` is on the tag immediately (default advisor palview).
   * Avoids waiting for the iframe advisor response, which is what caused the layout jump.
   */
  resolveTargetMode?: () => Promise<PalChatbotMode | null | undefined> | PalChatbotMode | null | undefined;
};

const applyPalChatbotTargetMode = (targetMode: PalChatbotMode): void => {
  applyPalChatbotHostMode(targetMode);
};

const resolveInitialPalMode = async (
  options?: InjectPalChatbotOptions
): Promise<PalChatbotMode> => {
  if (options?.resolveTargetMode) {
    try {
      const targetMode = await options.resolveTargetMode();
      if (targetMode === 'dock' || targetMode === 'widget') {
        return targetMode;
      }
    } catch {
      // fall through to session/default
    }
  }
  return getPalSessionMode();
};

const ensurePalChatbotScript = (scriptUrl: string): Promise<void> => {
  if (!scriptUrl) {
    return Promise.resolve();
  }
  const existing = document.querySelector(`script[src="${scriptUrl}"]`) as HTMLScriptElement | null;
  if (existing) {
    if (existing.dataset.palLoaded === 'true' || customElements.get('pal-chatbot')) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('PAL script failed to load')), {
        once: true,
      });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.addEventListener(
      'load',
      () => {
        script.dataset.palLoaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener('error', () => reject(new Error('PAL script failed to load')), {
      once: true,
    });
    document.body.appendChild(script);
  });
};

/**
 * Inject or refresh the PAL host web component.
 * A pure mode toggle (widget <-> dock) updates the `mode` attribute on the existing element in
 * place — the widget reacts to that itself — so the iframe/session is never torn down just to
 * flip modes.
 */
const injectPalChatbot = (
  CtyShowToast: (type: string, message: string) => void,
  checkType: any,
  widgetIconUrl?: string,
  options?: InjectPalChatbotOptions,
  mode?: PalChatbotMode
) => {
  const org = getPalEmbedOrg();
  const scriptUrl = getPalChatbotScriptUrl();
  const env = getEnv();
  const shouldOpen = options?.open !== false;

  const attachPalCloseListener = (element: Element): void => {
    if ((element as HTMLElement).dataset.palCloseListener === 'true') {
      return;
    }
    (element as HTMLElement).dataset.palCloseListener = 'true';
    // Keep the default-advisor mode on the tag across close/reopen so the next
    // open does not flash widget while waiting for advisor metadata.
  };

  const syncPalHostAttributes = (element: Element): void => {
    const pal = element as HTMLElement & { syncFromHostAttributes?: () => void };
    pal.syncFromHostAttributes?.();
  };

  const mountOrUpdatePalChatbot = (initialMode: PalChatbotMode = 'widget'): void => {
    let palChatbotElement = document.querySelector('pal-chatbot');
    if (!palChatbotElement) {
      palChatbotElement = document.createElement('pal-chatbot');
      palChatbotElement.setAttribute('org', org);
      palChatbotElement.setAttribute('env', env);
      palChatbotElement.setAttribute('mode', initialMode);
      palChatbotElement.setAttribute('close', options?.open ? 'false' : 'true');
      if (widgetIconUrl) {
        const icon = getIconName(widgetIconUrl);
        palChatbotElement.setAttribute('icon', icon as string);
      }
      document.body.appendChild(palChatbotElement);
      attachPalCloseListener(palChatbotElement);
      syncPalHostAttributes(palChatbotElement);
    } else {
      attachPalCloseListener(palChatbotElement);
      palChatbotElement.setAttribute('org', org);
      palChatbotElement.setAttribute('env', env);
      palChatbotElement.setAttribute('mode', initialMode);
      palChatbotElement.setAttribute('close', options?.open ? 'false' : 'true');
      syncPalHostAttributes(palChatbotElement);
    }
  };

  const reshapeToTargetMode = async (): Promise<void> => {
    if (!options?.resolveTargetMode) {
      return;
    }
    try {
      const targetMode = await options.resolveTargetMode();
      if (targetMode === 'dock' || targetMode === 'widget') {
        applyPalChatbotTargetMode(targetMode);
      }
    } catch {
      // keep current presentation on failure
    }
  };

  const openPalPanel = async (): Promise<void> => {
    await customElements.whenDefined('pal-chatbot');
    const initialMode = options?.resolveTargetMode
      ? await resolveInitialPalMode(options)
      : (mode ?? (await resolveInitialPalMode(options)));
    mountOrUpdatePalChatbot(initialMode);

    const palChatbot = document.querySelector('pal-chatbot') as HTMLElement & {
      openPanel?: () => void;
      closePanel?: () => void;
    } | null;

    if (typeof palChatbot?.openPanel === 'function') {
      palChatbot.openPanel();
      await reshapeToTargetMode();
      return;
    }

    const shadowRoot = palChatbot?.shadowRoot;
    if (shadowRoot) {
      if (initialMode === 'widget') {
        const palChatbotIcon = shadowRoot.querySelector('#pal-chatbot-icon');
        const chatBotWidth = shadowRoot.querySelector('#pal-chatbot');
        chatBotWidth && ((chatBotWidth as HTMLElement).style.width = '24rem');
        palChatbotIcon?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }
      await reshapeToTargetMode();
      return;
    }

    CtyShowToast(checkType.error, 'PAL Chatbot not found');
  };

  const mountClosedPalChatbot = async (): Promise<void> => {
    await customElements.whenDefined('pal-chatbot');
    const initialMode = mode ?? (await resolveInitialPalMode(options));
    mountOrUpdatePalChatbot(initialMode);
    await reshapeToTargetMode();
    const palChatbot = document.querySelector('pal-chatbot') as HTMLElement & {
      closePanel?: () => void;
    } | null;
    if (typeof palChatbot?.closePanel === 'function') {
      palChatbot.closePanel();
    }
    palChatbot?.removeAttribute('data-dock-open');
  };

  void ensurePalChatbotScript(scriptUrl)
    .then(() => {
      if (shouldOpen) {
        return openPalPanel();
      }
      return mountClosedPalChatbot();
    })
    .catch(() => {
      CtyShowToast(checkType.error, 'PAL Chatbot script failed to load');
    });
};

const sharedPDFViewHandler = async (
  loadingState: string,
  setPdfLoadingIndex: React.Dispatch<React.SetStateAction<string>>,
  apiPayLoad: any,
  getPdfLink: any,
  sourcId?: any,
  fileType?: any,
  source?: any,
  setInlineTriggerLoading?: any,
) => {
  // Pre-open a blank tab immediately for all file types (shows "Fetching file..." loading indicator)
  // Will be closed for file types that use window.open directly
  const preOpened = openPreOpenedTab(source?.file_name);

  try {
    setPdfLoadingIndex(loadingState);

    const urlReponse = await getPdfLink(apiPayLoad).unwrap();

    const fullPdfUrl = urlReponse?.data?.[0]?.full_source_presigned_url;
    const pagePdfUrl = urlReponse?.data?.[0]?.page_labels_presigned_url;
    const tabKey = Date.now().toString();
    switch (urlReponse?.data?.[0].source_extension) {
      case 'html': {
        // Check if this is a DITAMAP file (from element_refs)
        const isDitaMap = source?.element_refs?.[0]?.file_type === 'ditamap';
        
        if (isDitaMap) {
          // For DITAMAP files, route to the DITA HTML viewer for processing
          console.log('🔍 Detected DITAMAP file, routing to DITA viewer');
          
          const sourceFileName = urlReponse?.data?.[0]?.filename || 'ditamap-file.html';
          const advisorId = apiPayLoad?.advisor_id || source?.advisor_id || '';
          
          const pdfData = {
            initialPdfUrl: fullPdfUrl,
            fullPdfUrl: fullPdfUrl,
            fileType: 'ditahtml', // Single DITA HTML file from converse
            fileName: sourceFileName,
            sourceObject: source,
            advisorId: advisorId,
          };

          const packed = compressedData(pdfData);
          console.log('📦 Compressed pdfData:', { tabKey, advisorId, fileName: sourceFileName });

          if (applicationType() === ApplicationType.WEB) {
            if (preOpened && !preOpened.closed) {
              const targetUrl = `/file/${advisorId}/${encodeURIComponent(sourceFileName)}?tabKey=${tabKey}`;
              console.log('🔗 Navigating to:', targetUrl);
              
              try {
                // Try to set in the child window's sessionStorage (more reliable for same-origin)
                preOpened.sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
                console.log('✅ Stored pdfData in child window sessionStorage:', `pdfData-${tabKey}`);
              } catch (error) {
                console.warn('⚠️ Could not access child window sessionStorage, storing in parent:', error);
                // Fallback: store in parent window's sessionStorage
                try {
                  sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
                  console.log('✅ Stored pdfData in parent sessionStorage:', `pdfData-${tabKey}`);
                } catch (e) {
                  console.error('❌ Failed to store in sessionStorage:', e);
                }
              }
              
              preOpened.location.href = targetUrl;
            }
          } else if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
            // Extension context for DITAMAP: Store in chrome.storage.session and open extension tab
            //@ts-expect-error
            const fileViewerUrl = chrome.runtime.getURL(`/index.html#file/${advisorId}/${encodeURIComponent(sourceFileName)}?tabKey=${tabKey}`);
            await openExtensionTab(tabKey, fileViewerUrl, JSON.stringify(packed), preOpened);
          } else {
            // Widget context: Store in chrome.storage.session (if available) and sessionStorage
            const targetUrl = `/file/${advisorId}/${encodeURIComponent(sourceFileName)}?tabKey=${tabKey}`;
            await handleWidgetContextNavigation(preOpened, JSON.stringify(packed), targetUrl, tabKey);
          }
        } else {
          // For non-DITAMAP HTML files, open directly
          const newurl = `${urlReponse?.data?.[0]?.full_source_presigned_url}#${source?.element_refs?.[0]?.navigate_section ? source?.element_refs?.[0]?.navigate_section : ''}`;
          if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
            // preOpened is always null in the extension context (see openPreOpenedTab),
            // so open the presigned URL directly as a new extension tab
            closePreOpenedTab(preOpened);
            //@ts-expect-error
            chrome.tabs.create({ url: newurl });
          } else if (preOpened && !preOpened.closed) {
            preOpened.location.href = newurl;
          }
        }
        return null;
      }
      case 'mp4':
      case 'mp3':
      case 'webm':
        openMediaViewer(
          [source?.element_refs?.[0]?.start_time, source?.element_refs?.[0]?.end_time],
          fullPdfUrl,
          undefined,
          preOpened,
        );
        return null;
      case 'url': {
        const pageUrl = urlReponse?.data?.[0]?.url;
        if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
          closePreOpenedTab(preOpened);
          //@ts-expect-error
          chrome.tabs.create({ url: pageUrl });
        } else if (preOpened && !preOpened.closed && pageUrl) {
          preOpened.location.href = pageUrl;
        } else if (pageUrl) {
          window.open(pageUrl, '_blank');
        }
        return null;
      }
      case 'htm':
      case 'xml':
      case 'xlsx':
      case 'xls':
      case 'docx':
      case 'csv':
      case 'pptx':
      case 'ppt':
        // Close the preOpened tab and open directly with window.open
        closePreOpenedTab(preOpened);
        window.open(fullPdfUrl, '_blank');
        return null;
      default:
        break;
    }
    const sourceFileName = urlReponse?.data?.[0]?.filename;
    const viewerFileTYpe = fileType === 'parts_svg' ? 'parts_svg' : urlReponse?.data?.[0].source_extension;
    const filteredSource = sourcId?.filter((source: any) => source.source_id);
    const sourceId = urlReponse?.data?.[0]?.source_id;
    const pdfData = { initialPdfUrl: pagePdfUrl, fullPdfUrl: fullPdfUrl, fileType: viewerFileTYpe, responseSourceData: filteredSource, fileName: urlReponse?.data?.[0].filename, pageLabel: apiPayLoad?.source_ids?.[0]?.page_labels?.[0] ?? ['1'], sourceId: sourceId };
    const { advisorId } = (fullPdfUrl &&
      extractAdvisorIdAndFilename(fullPdfUrl)) ?? {
      advisorId: ''
    };
    const packed = compressedData(pdfData);
    console.log("applicationType", applicationType());
    if (applicationType() === ApplicationType.WEB) {
      const targetUrl = `/file/${advisorId}/${encodeURIComponent(sourceFileName)}?tabKey=${tabKey} `;
      if (preOpened && !preOpened.closed) {
        try {
          preOpened.sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
        } catch { /* noop */ }
        preOpened.location.href = targetUrl;
      }

    } else if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
      // Store in chrome.storage.session and open extension tab
      //@ts-expect-error
      const fileViewerUrl = chrome.runtime.getURL(`/index.html#file/${advisorId}/${encodeURIComponent(sourceFileName)}?tabKey=${tabKey}`);
      await openExtensionTab(tabKey, fileViewerUrl, JSON.stringify(packed), preOpened);
    } else {
      // Widget context: Store in chrome.storage.session (if available) and sessionStorage
      const targetUrl = `/file/${advisorId}/${encodeURIComponent(sourceFileName)}?tabKey=${tabKey} `;
      await handleWidgetContextNavigation(preOpened, JSON.stringify(packed), targetUrl, tabKey);
    }

  } catch (e) {
    CtyShowToast(
      checkType.error,
      (e as Error)?.message || 'Error while getting pdf link'
    );
    closePreOpenedTab(preOpened);
  } finally {
    setPdfLoadingIndex?.('');
    setInlineTriggerLoading?.('');
  }
};
const RichTextEditorThemeConfig = {
  code: 'editor-code',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
    h4: 'editor-heading-h4',
    h5: 'editor-heading-h5',
  },
  image: 'editor-image',
  link: 'editor-link',
  list: {
    listitem: 'editor-listitem',
    nested: {
      listitem: 'editor-nested-listitem',
    },
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
  },
  ltr: 'ltr',
  paragraph: 'editor-paragraph',
  placeholder: 'editor-placeholder',
  quote: 'editor-quote',
  rtl: 'rtl',
  text: {
    bold: 'editor-text-bold',
    code: 'editor-text-code',
    hashtag: 'editor-text-hashtag',
    italic: 'editor-text-italic',
    overflowed: 'editor-text-overflowed',
    strikethrough: 'editor-text-strikethrough',
    underline: 'editor-text-underline',
    underlineStrikethrough: 'editor-text-underlineStrikethrough',
  },
};
/**
 * Check if the current environment is a browser extension
 * @returns {boolean} True if running in Chrome or Firefox extension context
 */

const getUserInitials = (nickname = '') => nickname.split('.').filter(Boolean).map(word => word[0]?.toUpperCase() || '').join('').slice(0, 2);
const getUserName = (isAuthenticated: boolean, user: any) => isAuthenticated ? user?.name?.trim() ?? 'You' : 'Guest';
const textCapitalize = (str = '') => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

enum EnumFileStatus {
  ACTIVE = "active",
  UNTRAINED = "untrained",
  INACTIVE = "inactive",
  IN_PROGRESS = "in_progress",
  FAILED = "failed",
  UNLINKED = "unlinked",
  DELETED = "deleted",
  DELETION_IN_PROGRESS = "deletion_inprogress",
  DELETION_FAILED = "deletion_failed",
  UNLINKING_IN_PROGRESS = "unlink_inprogress",
  DRAFTED = "drafted",
  PENDING = "pending",
  PUBLISHED = "published",
  REJECTED = "rejected"
}

const fileStatusValues = {
  active: "Active",
  untrained: 'Untrained',
  inactive: "Inactive",
  in_progress: "In Progress",
  failed: "Failed",
  unlinked: 'Unlinked',
  deleted: "Deleted",
  deletion_inprogress: 'Deletion In Progress',
  validation_inprogress: 'Validation In Progress',
  validation_failed: 'Validation Failed',
  validation_success: 'Validation Success',
  unlink_inprogress: 'Unlink In Progress',
  unlink_scheduled: 'Unlink Scheduled',
  deletion_failed: 'Deletion Failed',
  drafted: 'Drafted',
  pending: 'Pending',
  published: 'Published',
  rejected: 'Rejected',
  approved: 'Approved',
  deactivated: "Deactivated"
};
const fileStatusColorValues = {
  inactive: "danger",
  untrained: 'default',
  active: "success",
  failed: "danger",
  in_progress: "warning",
  deleted: "danger",
  validation_failed: 'danger',
  validation_success: 'success',
  deletion_inprogress: 'warning',
  validation_inprogress: 'warning',
  unlink_inprogress: 'warning',
  deletion_failed: 'danger',
  unlinked: 'primary',
  drafted: 'default',
  pending: 'default',
  published: 'success',
  rejected: 'danger',
  unlink_scheduled: 'warning',
  deactivated: 'danger'
};
// Utility function for API calls
const apiCall = async (
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  headers: Record<string, string> = {},
  body?: Record<string, unknown>
): Promise<any> => {
  try {
    const options: RequestInit = {
      method,
      headers,
    };
    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json();
      console.error(`Error: ${response.status} ${response.statusText}`, errorData);
      throw new Error(response.statusText);
    }

    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

const integrateAdvisorAbilities = (advisorAbilitiesData: any) => {
  const specificAbilitiesSet = new Set(['auto_train', 'voice', 'translation', 'zapier', 'country']);

  const tenantAbilitiesMap = Object.fromEntries(
    (advisorAbilitiesData[0].tenant_abilities || []).map((ability: Setting) => [
      ability.ability,
      ability,
    ])
  );

  const advisorAbilities = (advisorAbilitiesData[0].abilities || []).reduce(
    (result: Record<SETTING_TYPE, Setting>, ability: Setting) => {
      const isSpecific = specificAbilitiesSet.has(ability.ability);
      const tenantAbility = tenantAbilitiesMap[ability.ability] as Setting | undefined;
      const isActive = tenantAbility?.is_active;

      if (!isSpecific || (isSpecific && isActive)) {
        result[ability.ability] = {
          ...ability,
          metadata: {
            ...tenantAbility?.metadata,
            ...ability.metadata,
          },
        };
      }
      return result;
    },
    {}
  );


  return advisorAbilities;
};

// Helper function to handle widget context navigation (stores data and navigates)
const handleWidgetContextNavigation = async (
  preOpened: Window | null,
  packed: string,
  targetUrl: string,
  tabKey: string
) => {
  // Primary: Store in extension storage (if available)
  if ((globalThis as any).chrome?.storage?.session) {
    try {
      await (globalThis as any).chrome.storage.session.set({
        [`pdfData-${tabKey}`]: packed
      });
    } catch (error) {
      console.warn('⚠️ Extension storage failed:', error);
    }
  }
  
  // Fallback: Store in opened window's sessionStorage
  if (preOpened && !preOpened.closed) {
    try {
      preOpened.sessionStorage.setItem(`pdfData-${tabKey}`, packed);
    } catch { /* noop */ }
    preOpened.location.href = targetUrl;
  }
};

// Helper function to navigate zip file viewer window (reusable navigation logic)
const navigateZipFileViewerWindow = async (
  preOpened: Window | null,
  pdfData: any,
  advisorId: string,
  fileName: string,
  tabKey: string
) => {
  const currentOrigin = window.location.origin;
  const packed = compressedData(pdfData);

  if (applicationType() === ApplicationType.WEB) {
    const targetUrl = `/file/${advisorId}/${encodeURIComponent(fileName)}?tabKey=${tabKey}&origin=${encodeURIComponent(currentOrigin)} `;
    if (preOpened && !preOpened.closed) {
      try {
        preOpened.sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
      } catch { /* noop */ }
      preOpened.location.href = targetUrl;
    }
  } else if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
    // Extension context: Store in chrome.storage.session and open extension tab
    //@ts-expect-error
    const fileViewerUrl = chrome.runtime.getURL(`/index.html#file/${advisorId}/${encodeURIComponent(fileName)}?tabKey=${tabKey}&origin=${encodeURIComponent(currentOrigin)}`);
    await openExtensionTab(tabKey, fileViewerUrl, JSON.stringify(packed), preOpened);
  } else {
    // Widget context: Store in chrome.storage.session (if available) and sessionStorage
    const targetUrl = `/file/${advisorId}/${encodeURIComponent(fileName)}?tabKey=${tabKey}&origin=${encodeURIComponent(currentOrigin)} `;
    await handleWidgetContextNavigation(preOpened, JSON.stringify(packed), targetUrl, tabKey);
  }
};

const handleZipFileViewer = async (item: any, fetchZipComponents: any, setIsTableFileLoading: any, loadingType: string, setInlineTriggerLoading: any) => {
  // Pre-open a blank tab synchronously to avoid popup blockers (only for WEB and WIDGET)
  const preOpened = openPreOpenedTab(item?.file_name);
  try {
    loadingType !== 'converse' && setIsTableFileLoading((prevState) => ({
      ...prevState,
      [item?.source_id]: {
        ...prevState[item?.source_id],
        [loadingType]: true,
      },
    }));
    const zipComponents = await fetchZipComponents(item?.source_id).unwrap();
    const tabKey = Date.now().toString();

    // Store both the PDF data and the authentication token 
    const pdfData = {
      zipSchema: zipComponents,
      responseSourceData: item?.source_id,
      fileName: item?.file_name ?? '',
      fileType: 'zip',
      // Add the original source object to ensure all needed data is available 
      sourceObject: item
    };

    // Navigate using the reusable helper function
    await navigateZipFileViewerWindow(preOpened, pdfData, item?.advisor_id, item?.file_name, tabKey);

  } catch (error) {
    console.error('Error fetching zip components:', error);
  } finally {
    setInlineTriggerLoading?.('');
    loadingType === 'converse' ? setIsTableFileLoading((prevState) => ({
      ...prevState,
      [item?.source_id]: {
        ...prevState[item?.source_id],
        [loadingType]: false,
      },
    })) : setIsTableFileLoading('');
  }
};

/**
 * Handles DITAMAP file viewing from knowledge tab
 * DITAMAP files have: source_extension === 'zip' AND source_type === 'ditamap'
 * Calls /api/v2/knowledge/view to get presigned URL which points to JSON TOC structure
 * Stores presigned URL in sessionStorage for DitaTreeViewer to fetch JSON on load
 * Opens in DitaTreeViewer with sidebar navigation (similar to ZIP viewer, no right panel)
 */
const handleDitamapFileViewer = async (
  item: any,
  getPdfLink: any,
  setIsTableFileLoading: any,
  loadingType: string,
  setInlineTriggerLoading?: any
) => {
  // Pre-open a blank tab synchronously to avoid popup blockers (only for WEB and WIDGET)
  const preOpened = openPreOpenedTab(item?.file_name);
  
  try {
    loadingType !== 'converse' && setIsTableFileLoading((prevState) => ({
      ...prevState,
      [item?.source_id]: {
        ...prevState[item?.source_id],
        [loadingType]: true,
      },
    }));

    // Call the knowledge view API to get presigned URL
    const payload = {
      source_ids: [{ source_id: item?.source_id }],
    };

    console.log('🔄 Fetching DITAMAP presigned URL for:', item?.source_id);
    
    const apiResult = getPdfLink(payload);
    const response =
      typeof apiResult.unwrap === 'function'
        ? await apiResult.unwrap()
        : await apiResult;

    const presignedUrl = response?.data?.[0]?.full_source_presigned_url;

    if (!presignedUrl) {
      throw new Error('No presigned URL returned for DITAMAP file');
    }

    console.log('✅ Got presigned URL for DITAMAP JSON');

    const tabKey = Date.now().toString();

    // Store presigned URL in pdfData - file viewer will fetch JSON from this URL
    const citedTopicSourceId = item?.element_refs?.[0]?.sub_source_id;
    const pdfData = {
      ditaJsonUrl: presignedUrl, // Presigned URL that returns JSON TOC structure
      fileName: item?.file_name ?? '',
      fileType: 'ditazipfile', // DITA ZIP file with table of contents
      sourceObject: item,
      advisorId: item?.advisor_id,
      ...(citedTopicSourceId ? { initialTopicSourceId: citedTopicSourceId } : {}),
    };

    // Store the data in session storage
    const currentOrigin = window.location.origin;
    const packed = compressedData(pdfData);

    const targetUrl = `/file/${item?.advisor_id}/${encodeURIComponent(item?.file_name)}?tabKey=${tabKey}&origin=${encodeURIComponent(currentOrigin)}`;
    
    if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
      // Extension context: Store in chrome.storage.session and open extension tab
      //@ts-expect-error
      const fileViewerUrl = chrome.runtime.getURL(`/index.html#file/${item?.advisor_id}/${encodeURIComponent(item?.file_name)}?tabKey=${tabKey}&origin=${encodeURIComponent(currentOrigin)}`);
      await openExtensionTab(tabKey, fileViewerUrl, JSON.stringify(packed), preOpened);
    } else if (preOpened && !preOpened.closed) {
      if (applicationType() === ApplicationType.WEB) {
        try {
          preOpened.sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
          console.log('✅ Stored DITAMAP presigned URL in sessionStorage');
        } catch (storageError) {
          console.warn('⚠️ Failed to store in child window sessionStorage:', storageError);
          // Fallback to parent window sessionStorage
          try {
            sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
            console.log('✅ Stored DITAMAP presigned URL in parent sessionStorage (fallback)');
          } catch (fallbackError) {
            console.error('❌ Failed to store in sessionStorage:', fallbackError);
          }
        }
      } else {
        // Widget context: Store in chrome.storage.session (if available) and sessionStorage
        // Primary: Store in extension storage (if available)
      if ((globalThis as any).chrome?.storage?.session) {
        try {
          await (globalThis as any).chrome.storage.session.set({
              [`pdfData-${tabKey}`]: JSON.stringify(packed)
            });
          } catch (error) {
            console.warn('⚠️ Extension storage failed:', error);
          }
        }
        
        // Fallback: Store in opened window's sessionStorage
        try {
          preOpened.sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
        } catch { /* noop */ }
      }
      preOpened.location.href = targetUrl;
    }

  } catch (error) {
    console.error('❌ Error fetching DITAMAP presigned URL:', error);
    if (preOpened && !preOpened.closed) {
      preOpened.close();
    }
    CtyShowToast(checkType.ERROR, 'Failed to load DITAMAP file');
  } finally {
    setInlineTriggerLoading?.('');
    if (loadingType === 'converse') {
      setIsTableFileLoading?.('');
    } else {
      setIsTableFileLoading((prevState) => ({
        ...prevState,
        [item?.source_id]: {
          ...prevState[item?.source_id],
          [loadingType]: false,
        },
      }));
    }
  }
};

const openDitaHtmlViewerTab = async ({
  sourceId,
  getPdfLink,
  advisorId,
  fileName,
  sourceObject,
  presignedUrl: preResolvedUrl,
  fileType = 'ditahtml',
}: {
  sourceId?: string;
  getPdfLink?: (payload: any) => Promise<any>;
  advisorId?: string;
  fileName?: string;
  sourceObject?: any;
  presignedUrl?: string;
  fileType?: string;
}) => {
  const preOpened = openPreOpenedTab(fileName);

  try {
    let presignedUrl = preResolvedUrl;
    let resolvedFileName = fileName;

    if (getPdfLink && sourceId) {
      try {
        const payload: any = { source_ids: [{ source_id: sourceId }] };
        if (advisorId) payload.advisor_id = advisorId;
        const apiResult = getPdfLink(payload);
        const response =
          typeof apiResult?.unwrap === 'function' ? await apiResult.unwrap() : await apiResult;
        const freshUrl = response?.data?.[0]?.full_source_presigned_url;
        if (freshUrl) presignedUrl = freshUrl;
        if (!resolvedFileName) resolvedFileName = response?.data?.[0]?.filename;
      } catch (fetchError) {
        console.warn('⚠️ openDitaHtmlViewerTab: Failed to refresh presigned URL, using cached URL', fetchError);
      }
    }

    if (!presignedUrl) {
      throw new Error('No presigned URL available for DITA link');
    }

    const { advisorId: extractedAdvisorId, filename: extractedFileName } =
      extractAdvisorIdAndFilename(presignedUrl) ?? { advisorId: '', filename: '' };
    const finalAdvisorId = advisorId || extractedAdvisorId || '';
    const finalFileName = resolvedFileName || extractedFileName || 'dita-topic.html';

    const tabKey = Date.now().toString();
    const pdfData = {
      initialPdfUrl: presignedUrl,
      fullPdfUrl: presignedUrl,
      fileType,
      fileName: finalFileName,
      sourceObject: sourceObject ?? { source_id: sourceId, advisor_id: finalAdvisorId },
      advisorId: finalAdvisorId,
    };

    const packed = compressedData(pdfData);
    const currentOrigin = window.location.origin;
    const targetUrl = `/file/${finalAdvisorId}/${encodeURIComponent(finalFileName)}?tabKey=${tabKey}&origin=${encodeURIComponent(currentOrigin)}`;

    if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
      //@ts-expect-error chrome runtime types are only present in extension builds
      const fileViewerUrl = chrome.runtime.getURL(`/index.html#file/${finalAdvisorId}/${encodeURIComponent(finalFileName)}?tabKey=${tabKey}&origin=${encodeURIComponent(currentOrigin)}`);
      await openExtensionTab(tabKey, fileViewerUrl, JSON.stringify(packed), preOpened);
    } else {
      if ((globalThis as any).chrome?.storage?.session) {
        try {
          await (globalThis as any).chrome.storage.session.set({
            [`pdfData-${tabKey}`]: JSON.stringify(packed),
          });
        } catch (storageError) {
          console.warn('⚠️ openDitaHtmlViewerTab: Extension storage failed:', storageError);
        }
      }
      if (preOpened && !preOpened.closed) {
        try {
          preOpened.sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
        } catch {
          try {
            sessionStorage.setItem(`pdfData-${tabKey}`, JSON.stringify(packed));
          } catch { /* noop */ }
        }
        preOpened.location.href = targetUrl;
      }
    }
  } catch (error) {
    console.error('❌ openDitaHtmlViewerTab: Failed to open DITA link in viewer:', error);
    closePreOpenedTab(preOpened);
    CtyShowToast(checkType.error, (error as Error)?.message || 'Failed to open DITA link');
  }
};

const formatLatency = (latency: number): string => {
  if (latency < 0.000001) {
    return `${(latency * 1000000000).toFixed(1)} ns`;
  } else if (latency < 0.001) {
    return `${(latency * 1000000).toFixed(1)} μs`;
  } else if (latency < 1) {
    return `${(latency * 1000).toFixed(1)} ms`;
  } else {
    return `${latency.toFixed(1)}s`;
  }
};

function formatFileSize(bytes: number) {
  const KB = 1024;
  const MB = KB * 1024;

  if (bytes >= MB) {
    return (bytes / MB).toFixed(2) + ' MB';
  } else {
    return (bytes / KB).toFixed(2) + ' KB';
  }
}

const getFileType = (filename: string) => {
  const name = filename.split('.');
  return name[name.length - 1];
};

const aggregateByCategory = (files: any) => {
  const categories = {
    Documents: ["doc", "docx", "pdf", "zip"],
    Spreadsheets: ["xls", "xlsx"],
    Presentations: ["ppt", "pptx"],
    "Web Links": ["url", "youtube", "html", "xml"],
    Media: ["mp3", "wav", "aac", "flac", "ogg", "m4a", "wma", "alac", "aiff", "mp4", "avi", "mkv", "mov", "wmv", "flv", "webm", "m4v", "mpeg", "3gp", "xml", "svg", "jpg", "jpeg", "png", "gif", "bmp", "tiff", "tif", "webp", "heic", "heif"]
  };

  const typeToCategory = Object.entries(categories)
    .flatMap(([cat, types]) => types.map(type => [type, cat]))
    .reduce((map, [type, cat]) => {
      map[type] = cat;
      return map;
    }, {});

  const result = Object.keys(categories).reduce((acc, cat) => {
    acc[cat] = { quantity: 0, size: 0.0, units: "MB" };
    return acc;
  }, {});

  for (const [type, { quantity, size }] of Object.entries(files)) {
    const cat = typeToCategory[type];
    if (cat) {
      result[cat].quantity += quantity;
      result[cat].size += size;
    }
  }

  return result;
}

const compressedData = (dataParam: any) => LZString.compressToEncodedURIComponent(JSON.stringify(dataParam));
const decompressedData = (dataParam: any) => JSON.parse(LZString.decompressFromEncodedURIComponent(dataParam));

const disableKeyUp = (e: React.KeyboardEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const disableKeyDown = (e: React.KeyboardEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

/** Format key for display: camelCase → "Part Requested", underscores → spaces. Linear runtime, no ReDoS. */
const formatKeyForDisplay = (key: string): string => {
  if (!key || typeof key !== 'string') return key;
  const len = key.length;
  const parts: string[] = [];
  let i = 0;
  while (i < len) {
    const c = key[i];
    const prev = i > 0 ? key[i - 1] : '';
    const next = i < len - 1 ? key[i + 1] : '';
    if (c === '_') {
      parts.push(' ');
      i += 1;
      continue;
    }
    const prevLower = prev >= 'a' && prev <= 'z';
    const prevUpper = prev >= 'A' && prev <= 'Z';
    const currUpper = c >= 'A' && c <= 'Z';
    const nextLower = next >= 'a' && next <= 'z';
    if (currUpper && prevLower) {
      parts.push(' ', c);
    } else if (currUpper && prevUpper && nextLower) {
      parts.push(' ', c);
    } else {
      parts.push(c);
    }
    i += 1;
  }
  const spaced = parts.join('').trim();
  const words: string[] = [];
  let start = 0;
  for (let j = 0; j <= spaced.length; j++) {
    if (j === spaced.length || spaced[j] === ' ') {
      if (j > start) words.push(spaced.slice(start, j));
      start = j + 1;
    }
  }
  return words.map((word) =>
    word.length > 1 && word === word.toUpperCase()
      ? word
      : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

const navigateToUxGenieView = async (tabKey: string, packed?: string) => {
  console.log('navigateToUxGenieView called with tabKey:', tabKey);
  if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
    const chrome = (globalThis as any).chrome;
    const storageKey = `templatePreview-${tabKey}`;
    const webAppBaseUrl: string = (import.meta as any).env?.CTY_APP_URL ?? 'https://pal.circuitry.ai';
    const webAppUrl = `${webAppBaseUrl}/#/uxgenie-view?tabKey=${tabKey}`;

    // Store in chrome.storage.local — no URL size limit, survives navigation
    if (packed && chrome?.storage?.local) {
      try {
        await chrome.storage.local.set({ [storageKey]: packed });
      } catch (error) {
        console.warn('⚠️ Failed to store template in chrome.storage.local:', error);
      }
    }

    // Open the web app tab
    const tab = await chrome.tabs.create({ url: webAppUrl });

    // Bridge: once the tab finishes loading, inject a script (ISOLATED world has
    // access to both chrome.storage and the page's sessionStorage) that copies
    // the data so TemplatePreviewPage can read it from sessionStorage.
    if (chrome?.scripting?.executeScript && tab?.id) {
      const onUpdated = (tabId: number, changeInfo: any) => {
        if (tabId === tab.id && changeInfo.status === 'complete') {
          chrome.tabs.onUpdated.removeListener(onUpdated);
          chrome.scripting.executeScript({
            target: { tabId: tab.id },
            world: 'ISOLATED',
            func: (key: string) => {
              (globalThis as any).chrome.storage.local.get([key]).then((result: any) => {
                const data = result[key];
                if (data) sessionStorage.setItem(key, data);
              });
            },
            args: [storageKey],
          }).catch((err: any) => {
            console.warn('⚠️ executeScript bridge failed:', err);
          });
        }
      };
      chrome.tabs.onUpdated.addListener(onUpdated);
    }
  }
}

interface UxGenieTemplatePayload {
  generatedCode: string;
  receivedClaimData: unknown;
  agentName: string;
}

interface OpenUxGenieTemplateOptions {
  onSessionStorageError?: () => void;
}

/**
 * Opens a UxGenie template preview in a new browser tab (web) or a chrome tab
 * (browser extension), packing the payload into compressed sessionStorage.
 * Shared between Claim Intake and Claim Score agent renderers to avoid
 * duplicated open-in-new-tab logic.
 */
const openUxGenieTemplateInNewTab = async (
  payload: UxGenieTemplatePayload,
  options: OpenUxGenieTemplateOptions = {}
): Promise<void> => {
  const tabKey = Date.now().toString();
  const packed = compressedData(payload);

  if (applicationType() === ApplicationType.BROWSER_EXTENSION) {
    await navigateToUxGenieView(tabKey, JSON.stringify(packed));
    return;
  }

  const preOpened = window.open('about:blank', '_blank');
  if (!preOpened) return;
  try {
    preOpened.sessionStorage.setItem(`templatePreview-${tabKey}`, JSON.stringify(packed));
  } catch {
    options.onSessionStorageError?.();
    preOpened.close();
    return;
  }
  const hasHashRouter = !!globalThis.location.hash?.startsWith('#/');
  preOpened.location.href = hasHashRouter
    ? `${globalThis.location.origin}${globalThis.location.pathname}${globalThis.location.search}#/uxgenie-view?tabKey=${tabKey}`
    : `${globalThis.location.origin}/uxgenie-view?tabKey=${tabKey}`;
  preOpened.focus();
};

const getIconName = (url: string) => {
  if (url.includes('cdn.circuitry.ai')) {
    const iconUrl = new URL(url);
    return iconUrl.pathname.split('/').pop();
  } else {
    return url;
  }
}

/** Prompt / chat attachment preview routing */
type AttachmentPreviewKind = 'image' | 'video' | 'audio' | 'file';

function getAttachmentPreviewKindFromFile(file: File): AttachmentPreviewKind {
  const mime = (file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'file';
}

function getAttachmentPreviewKindFromFileName(fileName: string): AttachmentPreviewKind {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  const image = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'tif', 'tiff'];
  const video = ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v', 'mpeg', 'mpg', '3gp', 'avi', 'mkv'];
  const audio = ['mp3', 'wav', 'aac', 'flac', 'm4a', 'opus'];
  if (image.includes(ext)) return 'image';
  if (video.includes(ext)) return 'video';
  if (audio.includes(ext)) return 'audio';
  return 'file';
}

function getDistinctAttachmentKindsFromSources(
  sources: Array<{ file_name?: string }> | null | undefined
): AttachmentPreviewKind[] {
  if (!Array.isArray(sources) || sources.length === 0) return [];
  return Array.from(
    new Set(
      sources.map((s) => getAttachmentPreviewKindFromFileName(s?.file_name ?? ''))
    )
  );
}

function getAttachmentKindIcon(kind: AttachmentPreviewKind): string {
  switch (kind) {
    case 'image':
      return 'ci:image-02';
    case 'video':
      return 'fa-regular:file-video';
    case 'audio':
      return 'fa:file-audio-o';
    default:
      return 'mi:document';
  }
}

function getDocumentCategoryLabelFromFile(file: File): string {
  const mime = (file.type || '').toLowerCase();
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (mime === 'application/pdf' || ext === 'pdf') return 'PDF';
  if (mime === 'application/zip' || mime === 'application/x-zip-compressed' || ext === 'zip') return 'ZIP';
  if (ext === 'csv' || mime === 'text/csv') return 'CSV';
  if (mime.includes('spreadsheetml') || ['xls', 'xlsx'].includes(ext)) return 'Excel';
  if (mime.includes('presentationml') || ['ppt', 'pptx'].includes(ext)) return 'PPT';
  if (mime.includes('wordprocessingml') || mime === 'application/msword' || ['doc', 'docx'].includes(ext)) return 'Document';
  if (mime.startsWith('text/') || ['txt', 'md', 'log', 'json', 'xml'].includes(ext)) return 'Text';
  return 'Other';
}

function getDocumentCategoryLabelFromFileName(fileName: string): string {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return 'PDF';
  if (ext === 'zip') return 'ZIP';
  if (ext === 'csv') return 'CSV';
  if (['xls', 'xlsx'].includes(ext)) return 'Excel';
  if (['ppt', 'pptx'].includes(ext)) return 'PPT';
  if (['doc', 'docx'].includes(ext)) return 'Document';
  if (['txt', 'md', 'log', 'json', 'xml'].includes(ext)) return 'Text';
  return 'Other';
}

type DateInputValue = string | number | Date | null | undefined;

const MONTH_SHORT_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

const toDateValue = (value: DateInputValue): Date | null => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const padDatePart = (value: number): string => String(value).padStart(2, '0');

/**
 * Converts input date to the given format pattern.
 * Supported tokens: yyyy, MMM, MM, dd, HH, mm, ss
 */
function convertDateFormat(value: DateInputValue, format: string): string {
  const parsedDate = toDateValue(value);
  if (!parsedDate || !format) return '';

  const replacements: Record<string, string> = {
    yyyy: String(parsedDate.getFullYear()),
    MMM: MONTH_SHORT_NAMES[parsedDate.getMonth()],
    MM: padDatePart(parsedDate.getMonth() + 1),
    dd: padDatePart(parsedDate.getDate()),
    HH: padDatePart(parsedDate.getHours()),
    mm: padDatePart(parsedDate.getMinutes()),
    ss: padDatePart(parsedDate.getSeconds()),
  };

  return format.replace(/yyyy|MMM|MM|dd|HH|mm|ss/g, (token) => replacements[token] ?? token);
}

/**
 * Coerce an arbitrary label into a valid lowercase snake_case tool name.
 *
 * @example
 * normalizeToolName('PCRS getClaimDetails') // 'pcrs_get_claim_details'
 * normalizeToolName('Get Description!!')     // 'get_description'
 * normalizeToolName('  Foo-Bar  ')           // 'foo_bar'
 */
function collapseUnderscoreSeparators(value: string): string {
  const segments: string[] = [];
  let segment = '';

  for (const char of value) {
    if (char === '_') {
      if (segment) {
        segments.push(segment);
        segment = '';
      }
      continue;
    }
    segment += char;
  }

  if (segment) {
    segments.push(segment);
  }

  return segments.join('_');
}

function normalizeToolName(raw: string): string {
  let s = (raw ?? '').trim();

  // Split camelCase / PascalCase boundaries into underscores (before lowercasing).
  s = s.replace(/([a-z0-9])([A-Z])/g, '$1_$2');
  s = s.replace(/([A-Z])([A-Z][a-z])/g, '$1_$2');
  s = s.toLowerCase();
  s = s.replace(/[^a-z0-9]+/g, '_');
  s = collapseUnderscoreSeparators(s);

  if (s && !/^[a-z]/.test(s[0])) {
    s = `a_${s}`;
  }

  return s;
}

export {
  formatPageLabels,
  getSourceClipTimeLabel,
  openPreOpenedTab,
  handleKnowledgePdfViewer,
  aggridCommonGridStyles,
  generateCircuitryLogoSvgBlobUrl,
  extractAdvisorIdAndFilename,
  injectPalChatbot,
  getPalChatbotScriptUrl,
  getPalEmbedOrg,
  getPalUrl,
  sharedPDFViewHandler, getUserInitials, getUserName,
  RichTextEditorThemeConfig,
  textCapitalize,
  EnumFileStatus,
  fileStatusValues,
  fileStatusColorValues,
  apiCall,
  integrateAdvisorAbilities,
  handleZipFileViewer,
  navigateZipFileViewerWindow,
  handleDitamapFileViewer,
  openDitaHtmlViewerTab,
  formatLatency,
  formatFileSize,
  getFileType,
  aggregateByCategory,
  compressedData,
  decompressedData,
  disableKeyUp,
  disableKeyDown,
  formatKeyForDisplay,
  navigateToUxGenieView,
  openUxGenieTemplateInNewTab,
  getIconName,
  getEnv,
  getAttachmentPreviewKindFromFile,
  getAttachmentPreviewKindFromFileName,
  getDistinctAttachmentKindsFromSources,
  getAttachmentKindIcon,
  getDocumentCategoryLabelFromFile,
  getDocumentCategoryLabelFromFileName,
  convertDateFormat,
  normalizeToolName,
};
export type { AttachmentPreviewKind };